// Pure helpers extracted from equipment-screen.js so they can be unit-tested
// in node without dragging in DOM-only dependencies (app-viewport →
// router.js touches `window` at module load).

/**
 * @typedef {{ attackBundledSwap: string, objectInteraction: string, action: string, explorationFree: boolean }} EquipmentBudget
 */

/**
 * Decide what UX path the player gets after dropping a card on a valid slot.
 *
 * Out of combat, drops commit silently with `tier=exploration`. In combat,
 * the rules-faithful menu (`modal`) is the one we surface when the player
 * still has their Attack action — because that unlocks the free
 * Attack-bundled swap. When Attack is gone but free Object Interaction
 * is still available, we commit silently with `tier=interaction` — the
 * player has only one realistic option, no ceremony needed. Anything
 * tighter (Action+Interaction both spent, Attack already used) → reject.
 *
 * @param {EquipmentBudget | null | undefined} budget
 * @returns {{ kind: 'commit', tier: string } | { kind: 'modal' } | { kind: 'reject' }}
 */
export function decideEquipPath(budget) {
  if (!budget) return { kind: 'reject' };
  if (budget.explorationFree) return { kind: 'commit', tier: 'exploration' };
  if (budget.attackBundledSwap === 'available') return { kind: 'modal' };
  if (budget.objectInteraction === 'available') return { kind: 'commit', tier: 'interaction' };
  return { kind: 'reject' };
}

/**
 * Label and tier for the second non-Attack option in the equip modal.
 * Drew's preference: prefer the free Object Interaction when it's still
 * available; fall back to spending the Action via Utilize; show no
 * second option when both are gone.
 *
 * @param {EquipmentBudget | null | undefined} budget
 * @returns {{ tier: string, label: string } | null}
 */
export function alternateEquipOption(budget) {
  if (!budget) return null;
  if (budget.objectInteraction === 'available') {
    return { tier: 'interaction', label: 'Equip with free interaction' };
  }
  if (budget.action === 'available') {
    return { tier: 'action', label: 'Equip with Utilize action' };
  }
  return null;
}

/**
 * True if the inventory item declares the slot as a valid landing spot.
 *
 * @param {{ equippableSlots?: string[] } | null | undefined} item
 * @param {string} slot
 */
export function itemAcceptsSlot(item, slot) {
  return Array.isArray(item?.equippableSlots) && item.equippableSlots.includes(slot);
}

/**
 * True when the player has no remaining tier to spend on an equipment
 * change. Combat-only — out of combat, swaps are unlimited via the
 * exploration-free branch.
 *
 * @param {EquipmentBudget | null | undefined} budget
 */
export function noEquipmentChangesLeft(budget) {
  if (!budget) return true;
  if (budget.explorationFree) return false;
  if (budget.attackBundledSwap === 'available') return false;
  if (budget.objectInteraction === 'available') return false;
  if (budget.action === 'available') return false;
  return true;
}
